import {
  bandCountsUnique,
  collapsedBand,
  collapsedCategoryKey,
  collapsedSubCategoryKey,
  rollupCollapsedByAttribute,
  rollupCollapsedByLocation,
  type CollapsedSupplierRisk,
  type RollupRow,
} from './supplierRollup'

export type PortfolioInsight = {
  id: string
  /** Short label for screen readers / hierarchy */
  kind: 'exposure' | 'hotspot' | 'mitigation' | 'single_source' | 'data_quality'
  text: string
}

/** Do not include bare "na" — that is the North America region code. */
const UNCLASSIFIED_KEYS = new Set(['unclassified', 'other', 'not available', 'n/a'])

function isUnclassifiedKey(key: string): boolean {
  return UNCLASSIFIED_KEYS.has(key.trim().toLowerCase())
}

function pct(part: number, whole: number): number {
  if (whole <= 0) return 0
  return Math.round((part / whole) * 100)
}

function topHighRollup(rows: RollupRow[]): RollupRow | null {
  const ranked = [...rows]
    .filter((r) => !isUnclassifiedKey(r.key) && r.high > 0)
    .sort((a, b) => b.high - a.high || b.count - a.count || a.key.localeCompare(b.key))
  return ranked[0] ?? null
}

export type BuildPortfolioInsightsOptions = {
  collapsed: CollapsedSupplierRisk[]
  /** Unique high-risk suppliers that have ≥1 open mitigation. */
  highWithOpenMitigation?: number
  /** Prefer country hotspot (Intelligence) vs category-led (Overview). */
  emphasizeCountry?: boolean
}

/**
 * Build 2–4 decision-oriented insight statements from the filtered portfolio.
 * Avoids restating raw KPI card values alone — each line ties a metric to meaning.
 */
export function buildPortfolioInsights(options: BuildPortfolioInsightsOptions): PortfolioInsight[] {
  const { collapsed, highWithOpenMitigation = 0, emphasizeCountry = false } = options
  const bands = bandCountsUnique(collapsed)
  const out: PortfolioInsight[] = []

  if (bands.total === 0) {
    return [
      {
        id: 'empty',
        kind: 'exposure',
        text: 'No suppliers match the current filters — clear or broaden filters to see portfolio insights.',
      },
    ]
  }

  const highPct = pct(bands.high, bands.total)
  out.push({
    id: 'exposure',
    kind: 'exposure',
    text:
      bands.high === 0
        ? `No high-risk suppliers in this filtered set of ${bands.total}.`
        : `${bands.high} of ${bands.total} suppliers (${highPct}%) are high risk — the primary exceptions to watch first.`,
  })

  const byCategory = rollupCollapsedByAttribute(collapsed, collapsedCategoryKey)
  const byCountry = rollupCollapsedByLocation(collapsed)
  const bySub = rollupCollapsedByAttribute(collapsed, collapsedSubCategoryKey)

  const topCat = topHighRollup(byCategory)
  const topCountry = topHighRollup(byCountry)
  const topSub = topHighRollup(bySub)

  if (emphasizeCountry && topCountry) {
    const share = pct(topCountry.high, bands.high || 1)
    let text = `Country hotspot: ${topCountry.key} holds ${topCountry.high} high-risk supplier${topCountry.high === 1 ? '' : 's'} (${share}% of high risk in this filter).`
    if (topCat) {
      text += ` Leading category: ${topCat.key} (${topCat.high} high).`
    }
    out.push({ id: 'hotspot', kind: 'hotspot', text })
  } else if (topCat) {
    const share = pct(topCat.high, bands.high || 1)
    let text = `Risk hotspot: ${topCat.key} concentrates ${topCat.high} high-risk supplier${topCat.high === 1 ? '' : 's'} (${share}% of high risk in this filter).`
    if (topSub && !isUnclassifiedKey(topSub.key) && topSub.high >= 2) {
      text += ` Densest sub-category cluster: ${topSub.key} (${topSub.high} high).`
    }
    out.push({ id: 'hotspot', kind: 'hotspot', text })
  }

  if (bands.high > 0) {
    const coveredPct = pct(highWithOpenMitigation, bands.high)
    const uncovered = Math.max(0, bands.high - highWithOpenMitigation)
    out.push({
      id: 'mitigation',
      kind: 'mitigation',
      text:
        highWithOpenMitigation === 0
          ? `None of the ${bands.high} high-risk suppliers currently have an open mitigation — coverage gap on the exceptions.`
          : `${highWithOpenMitigation} of ${bands.high} high-risk suppliers (${coveredPct}%) have open mitigations; ${uncovered} still ${uncovered === 1 ? 'lacks' : 'lack'} an open action.`,
    })
  }

  const highRisk = collapsed.filter((s) => collapsedBand(s) === 'high')
  const singleSourceHigh = highRisk.filter((s) => s.single_source === true).length
  if (singleSourceHigh > 0) {
    out.push({
      id: 'single-source',
      kind: 'single_source',
      text: `${singleSourceHigh} high-risk supplier${singleSourceHigh === 1 ? ' is' : 's are'} single-source — elevated disruption exposure if those sites fail.`,
    })
  }

  const priority = ['empty', 'exposure', 'hotspot', 'mitigation', 'single-source']
  out.sort((a, b) => priority.indexOf(a.id) - priority.indexOf(b.id))
  return out.slice(0, 4)
}
